// Section 24.15: every store action is wrapped by withBoundary, which catches SimError,
// restores the last autosave, sets ui.error with a diagnostics bundle, and re-throws anything
// that is not a SimError (a real bug should surface in dev tools).
import { contentVersion, isSimError, type GameState, type SimError } from '../sim/api';
import { currentRun } from './run';
import { restoreLatest } from './session';
import { game, patchUi } from './state';

export const APP_VERSION = '0.1.0';

/** The 24.15 diagnostics bundle; enough to reproduce with `npm run repro -- diagnostics.json`. */
export interface Diagnostics {
  appVersion: string;
  contentVersion: string;
  seed: string | null;
  options: GameState['options'] | null;
  turn: number | null;
  phaseCursor: string | null;
  decisionLog: GameState['decisionLog'];
  errorCode: string;
  stack: string;
  /** Present when the run is played on a saved (older) config. */
  configSnapshot?: unknown;
}

export function diagnosticsOf(
  state: GameState | null,
  errorCode: string,
  stack: string,
): Diagnostics {
  const cfg = currentRun().cfg;
  return {
    appVersion: APP_VERSION,
    contentVersion: contentVersion(),
    seed: state?.seed ?? null,
    options: state?.options ?? null,
    turn: state?.turn ?? null,
    phaseCursor: state?.phaseCursor ?? null,
    decisionLog: state?.decisionLog ?? [],
    errorCode,
    stack,
    ...(cfg === undefined ? {} : { configSnapshot: cfg }),
  };
}

function report(error: SimError, before: GameState | null): void {
  patchUi({
    error: {
      code: error.code,
      message: error.message,
      diagnostics: JSON.stringify(diagnosticsOf(before, error.code, error.stack ?? '')),
    },
  });
}

export function withBoundary<A extends unknown[], R>(
  action: (...args: A) => R,
): (...args: A) => R | undefined {
  return (...args: A) => {
    try {
      return action(...args);
    } catch (error) {
      if (!isSimError(error)) throw error;
      const before = game.value;
      // bad_status is thrown before anything changed; keep the game and the draft (F-05).
      if (error.code !== 'bad_status') restoreLatest();
      report(error, before);
      return undefined;
    }
  };
}
